import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Check, Terminal, ArrowRight } from "lucide-react";

const CY = "#00F0FF";

function Copyable({ code, id }) {
  const [c, setC] = useState(false);
  return (
    <div className="group relative rounded-lg border border-white/10 bg-black/60">
      <button
        onClick={() => { navigator.clipboard.writeText(code); setC(true); setTimeout(() => setC(false), 1500); }}
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-white/15 px-2 py-1 text-[11px] text-white/70 hover:bg-white/10"
        data-testid={`docs-copy-${id}`}>
        {c ? <Check size={12} /> : <Copy size={12} />}{c ? "Copied" : "Copy"}
      </button>
      <pre className="overflow-x-auto p-4 pr-20 font-mono text-[12px] leading-relaxed text-white/80"><code>{code}</code></pre>
    </div>
  );
}

const NAV = [
  ["quickstart", "Quick Start"], ["sdks", "SDKs"], ["auth", "Authentication"], ["models", "Models"],
  ["generate", "Generate Image"], ["edit", "Edit / Reference Images"], ["variations", "Variations & Upscale"], ["async", "Async Jobs"],
  ["get", "Get a Generation"], ["webhooks", "Webhooks"], ["credits", "Credits & Rate Limits"],
  ["idempotency", "Idempotency"], ["errors", "Errors"], ["sdk", "Code Examples"],
];

export default function ApiDocs() {
  const navigate = useNavigate();
  const origin = typeof window !== "undefined" ? window.location.origin : "https://api.captionmotion.com";
  const base = `${origin}/api/v1`;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-5 py-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 font-display text-lg font-black">
            <Terminal size={18} color={CY} /> CaptionMotion <span className="text-white/40">API Docs</span>
          </button>
          <button onClick={() => navigate("/developer")} className="inline-flex items-center gap-1.5 rounded-full bg-[#00F0FF] px-4 py-2 text-[13px] font-bold text-black" data-testid="docs-get-keys">
            Get API Keys <ArrowRight size={14} />
          </button>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1100px] gap-8 px-5 py-8">
        <nav className="sticky top-20 hidden h-fit w-52 shrink-0 space-y-1 lg:block">
          {NAV.map(([id, label]) => (
            <a key={id} href={`#${id}`} className="block rounded-md px-3 py-1.5 text-[13px] text-white/50 hover:bg-white/5 hover:text-white">{label}</a>
          ))}
        </nav>

        <main className="min-w-0 flex-1 space-y-12 text-[14px] leading-relaxed text-white/70" data-testid="api-docs">
          <section>
            <h1 className="font-display text-4xl font-black text-white">CaptionMotion AI Image API</h1>
            <p className="mt-3 max-w-2xl text-white/60">Generate and edit images from any application with a single stable model — <span className="font-mono text-[#00F0FF]">captionmotion-ai-image-model</span>. CaptionMotion is the API abstraction; the underlying provider may evolve over time without changing your integration.</p>
            <div className="mt-4 rounded-lg border border-white/10 bg-[#141418] p-4 font-mono text-[12px]">Base URL &nbsp;<span className="text-[#00F0FF]">{base}</span></div>
          </section>

          <section id="quickstart">
            <h2 className="font-display text-2xl font-bold text-white">Quick Start</h2>
            <ol className="mt-3 list-inside list-decimal space-y-1">
              <li>Sign in and open the <button onClick={() => navigate("/developer")} className="text-[#00F0FF] underline">Developer Portal</button>.</li>
              <li>Create an API key — copy your <span className="font-mono">cm_live_…</span> secret (shown once).</li>
              <li>Make your first request:</li>
            </ol>
            <div className="mt-3"><Copyable id="qs" code={`curl -X POST "${base}/images/generate" \\
  -H "Authorization: Bearer cm_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"captionmotion-ai-image-model","prompt":"A neon cyberpunk city at night","aspect_ratio":"16:9"}'`} /></div>
          </section>

          <section id="sdks">
            <h2 className="font-display text-2xl font-bold text-white">Official SDKs</h2>
            <p className="mt-2">Integrate in two lines with the first-party clients.</p>
            <p className="mt-3 text-white/50">JavaScript / TypeScript</p>
            <div className="mt-2"><Copyable id="sdkjs" code={`npm install captionmotion

import { CaptionMotion } from "captionmotion";
const cm = new CaptionMotion("cm_live_xxxx", { baseUrl: "${base}" });
const r = await cm.generate({ prompt: "A cozy coffee shop, warm light", aspect_ratio: "1:1" });
console.log(r.images[0].url);`} /></div>
            <p className="mt-4 text-white/50">Python</p>
            <div className="mt-2"><Copyable id="sdkpy" code={`pip install captionmotion

from captionmotion import CaptionMotion
cm = CaptionMotion("cm_live_xxxx", base_url="${base}")
r = cm.generate(prompt="A neon cyberpunk street", aspect_ratio="16:9")
print(r["images"][0]["url"])`} /></div>
          </section>

          <section id="auth">
            <h2 className="font-display text-2xl font-bold text-white">Authentication</h2>
            <p className="mt-2">Send your key as a Bearer token. Use <span className="font-mono">cm_test_</span> keys while developing and <span className="font-mono">cm_live_</span> keys in production. Keys are secret — never expose them in browser code.</p>
            <div className="mt-3"><Copyable id="auth" code={`Authorization: Bearer cm_live_xxxxxxxxxxxx`} /></div>
          </section>

          <section id="models">
            <h2 className="font-display text-2xl font-bold text-white">Models</h2>
            <p className="mt-2"><span className="font-mono">GET /v1/models</span> — lists the models your key can use. The public product model is always <span className="font-mono text-[#00F0FF]">captionmotion-ai-image-model</span>.</p>
            <div className="mt-3"><Copyable id="models" code={`curl "${base}/models" -H "Authorization: Bearer cm_live_xxxx"`} /></div>
          </section>

          <section id="generate">
            <h2 className="font-display text-2xl font-bold text-white">Generate an image</h2>
            <p className="mt-2"><span className="font-mono">POST /v1/images/generate</span>. Parameters: <span className="font-mono">prompt</span> (required), <span className="font-mono">aspect_ratio</span>, <span className="font-mono">resolution</span>, <span className="font-mono">number_of_images</span>, <span className="font-mono">reference_images</span>, <span className="font-mono">async</span>.</p>
            <div className="mt-3"><Copyable id="gen" code={`curl -X POST "${base}/images/generate" \\
  -H "Authorization: Bearer cm_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "captionmotion-ai-image-model",
    "prompt": "A premium product portrait, studio lighting",
    "aspect_ratio": "4:5",
    "resolution": 1024
  }'`} /></div>
            <p className="mt-3 text-white/50">Response:</p>
            <div className="mt-2"><Copyable id="genres" code={`{
  "id": "imgreq_...",
  "model": "captionmotion-ai-image-model",
  "status": "completed",
  "images": [{ "id": "img_...", "url": "https://...", "width": 819, "height": 1024 }],
  "usage": { "credits_used": 10 }
}`} /></div>
          </section>

          <section id="edit">
            <h2 className="font-display text-2xl font-bold text-white">Edit / Reference images</h2>
            <p className="mt-2">Supply up to 4 base64-encoded <span className="font-mono">reference_images</span> (face, product, style or composition) to guide the result. Use <span className="font-mono">POST /v1/images/edit</span> (requires the <span className="font-mono">image.edit</span> permission) or pass <span className="font-mono">reference_images</span> to <span className="font-mono">/generate</span>.</p>
            <div className="mt-3"><Copyable id="edit" code={`curl -X POST "${base}/images/edit" \\
  -H "Authorization: Bearer cm_live_xxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "captionmotion-ai-image-model",
    "prompt": "Place this product on a marble kitchen counter",
    "reference_images": ["<base64-image>"]
  }'`} /></div>
          </section>

          <section id="variations">
            <h2 className="font-display text-2xl font-bold text-white">Variations & Upscale</h2>
            <p className="mt-2"><span className="font-mono">POST /v1/images/variations</span> creates a fresh take from a source reference (requires the <span className="font-mono">image.variation</span> permission). <span className="font-mono">POST /v1/images/upscale</span> sharpens a result 2× or 4× — pass a base64 image or a prior <span className="font-mono">imgreq_…</span> id.</p>
            <div className="mt-3"><Copyable id="up" code={`curl -X POST "${base}/images/upscale" \\
  -H "Authorization: Bearer cm_live_xxxx" -H "Content-Type: application/json" \\
  -d '{"image":"imgreq_123","scale":4}'`} /></div>
          </section>

          <section id="async">
            <h2 className="font-display text-2xl font-bold text-white">Async jobs</h2>
            <p className="mt-2">Set <span className="font-mono">"async": true</span> (or header <span className="font-mono">X-Async: true</span>) to queue a job and return immediately. Poll <span className="font-mono">GET /v1/images/&#123;id&#125;</span> until <span className="font-mono">status</span> is <span className="font-mono">completed</span> or <span className="font-mono">failed</span>. Credits are reserved up front and refunded automatically if the job fails.</p>
            <div className="mt-3"><Copyable id="async" code={`# 1) queue
curl -X POST "${base}/images/generate" \\
  -H "Authorization: Bearer cm_live_xxxx" -H "Content-Type: application/json" \\
  -d '{"prompt":"A misty mountain sunrise","async":true}'
# -> { "id": "imgreq_...", "status": "queued" }

# 2) poll
curl "${base}/images/imgreq_..." -H "Authorization: Bearer cm_live_xxxx"
# -> { "status": "completed", "images": [...] }`} /></div>
          </section>

          <section id="get">
            <h2 className="font-display text-2xl font-bold text-white">Get a generation</h2>
            <p className="mt-2"><span className="font-mono">GET /v1/images/&#123;id&#125;</span> returns the current status and images for one of your generations.</p>
          </section>

          <section id="webhooks">
            <h2 className="font-display text-2xl font-bold text-white">Webhooks</h2>
            <p className="mt-2">Register endpoints in the Developer Portal. On <span className="font-mono">image.completed</span> / <span className="font-mono">image.failed</span> we POST a JSON event with an HMAC signature header. Verify it with your signing secret; retries use exponential backoff.</p>
            <div className="mt-3"><Copyable id="wh" code={`X-CM-Signature: sha256=<hex>
X-CM-Event: image.completed

# verify (Python)
import hmac, hashlib
expected = "sha256=" + hmac.new(secret.encode(), raw_body, hashlib.sha256).hexdigest()
assert hmac.compare_digest(expected, request.headers["X-CM-Signature"])`} /></div>
          </section>

          <section id="credits">
            <h2 className="font-display text-2xl font-bold text-white">Credits & rate limits</h2>
            <p className="mt-2">Each generation consumes credits (configurable; typically 10). Check your balance and usage via <span className="font-mono">GET /v1/account</span> and <span className="font-mono">GET /v1/usage</span>. Requests are rate-limited per key/plan; exceeding a limit returns <span className="font-mono">429</span> with a <span className="font-mono">Retry-After</span> header.</p>
          </section>

          <section id="idempotency">
            <h2 className="font-display text-2xl font-bold text-white">Idempotency</h2>
            <p className="mt-2">Send an <span className="font-mono">Idempotency-Key</span> header to safely retry requests — a repeated key returns the original result and never double-charges.</p>
            <div className="mt-3"><Copyable id="idem" code={`-H "Idempotency-Key: 9f1c2b7a-..."`} /></div>
          </section>

          <section id="errors">
            <h2 className="font-display text-2xl font-bold text-white">Errors</h2>
            <p className="mt-2">All errors return <span className="font-mono">{`{ "error": { "type", "message" } }`}</span> with an HTTP status:</p>
            <div className="mt-3 overflow-hidden rounded-lg border border-white/10">
              <table className="w-full text-left text-[13px]">
                <thead className="bg-[#141418] text-[11px] uppercase text-white/40"><tr><th className="px-4 py-2">Status</th><th className="px-4 py-2">Type</th></tr></thead>
                <tbody className="font-mono text-white/60">
                  {[["401", "INVALID_API_KEY"], ["402", "INSUFFICIENT_CREDITS"], ["403", "INSUFFICIENT_PERMISSION"], ["404", "MODEL_NOT_FOUND / NOT_FOUND"], ["422", "INVALID_REQUEST / UNSUPPORTED_PARAMETER / UNSUPPORTED_IMAGE"], ["429", "RATE_LIMITED"], ["500", "INTERNAL_ERROR"]].map(([s, t]) => (
                    <tr key={s} className="border-t border-white/5"><td className="px-4 py-2 text-[#00F0FF]">{s}</td><td className="px-4 py-2">{t}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section id="sdk">
            <h2 className="font-display text-2xl font-bold text-white">Code examples</h2>
            <p className="mt-2 text-white/50">JavaScript (fetch):</p>
            <div className="mt-2"><Copyable id="js" code={`const r = await fetch("${base}/images/generate", {
  method: "POST",
  headers: { Authorization: "Bearer cm_live_xxxx", "Content-Type": "application/json" },
  body: JSON.stringify({ model: "captionmotion-ai-image-model", prompt: "A cozy coffee shop, warm light", aspect_ratio: "1:1" }),
});
const data = await r.json();
console.log(data.images[0].url);`} /></div>
            <p className="mt-4 text-white/50">Python (requests):</p>
            <div className="mt-2"><Copyable id="py" code={`import requests
r = requests.post("${base}/images/generate",
    headers={"Authorization": "Bearer cm_live_xxxx"},
    json={"model": "captionmotion-ai-image-model", "prompt": "A neon cyberpunk street", "aspect_ratio": "16:9"})
print(r.json()["images"][0]["url"])`} /></div>
          </section>

          <div className="border-t border-white/10 pt-8 text-[13px] text-white/40">
            CaptionMotion provides the API and abstraction. The underlying image model may change over time to improve quality — your integration and <span className="font-mono">captionmotion-ai-image-model</span> stay the same.
          </div>
        </main>
      </div>
    </div>
  );
}
